import { launchDicomViewerForReader } from '@common/utils/ohifUtils'
import { TableBody, TableCell, TableRow, Typography } from '@mui/material'
import { useAssignTasksMutation } from '@reader_task_list_view/hooks/useAssignTasksMutation'
import { EnhancedTableBodyProps } from '@reader_task_list_view/reader_task_list.types'
import { getComparator } from '@reader_task_list_view/utils'
import { AxiosError } from 'axios'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import TaskStateChip from './TaskStateChip'
import TaskTypeChip from './TaskTypeChip'
import Toast from './Toast'
import { formatTaskDate } from '@qc_task_list_view/utils'
import { useSigningContext } from '@features/reader_task_list_iam_view/pages/hooks/useSigningContext'

export default function EnhancedTableBodyTaskPool({
  rows,
  order,
  orderBy,
  authedUserToken,
  authedUserId,
  isRawDataEnabled,
  isOphthoEnabled,
}: EnhancedTableBodyProps) {
  const visibleRows = useMemo(() => [...rows].sort(getComparator(order, orderBy)), [order, orderBy, rows])
  const { mutateAsync: assignTask } = useAssignTasksMutation()
  const { studyId } = useParams()

  const { openSigningModal } = useSigningContext()

  const handleAssignTask = async (task_id: string, task_type: string) => {
    if (authedUserId && studyId && authedUserToken && task_id) {
      await assignTask({
        query: {
          read_task_ids: [task_id],
          study_id: studyId,
        },
      })
        .then((res) => {
          toast(
            <Toast
              message="You were assigned task"
              task_ids={res.processed_task_ids.map((task) => {
                return task.split('-')[0]
              })}
              variant="success"
              data-testid="success-toast"
            />,
          )

          if (task_type !== 'ADJUDICATION') {
            launchDicomViewerForReader(task_id, studyId, isRawDataEnabled ? 2 : isOphthoEnabled ? 3 : 0)
          }

          openSigningModal(task_id, isRawDataEnabled)
        })
        .catch((error) => {
          toast(<Toast message={(error as AxiosError).message} variant="error" data-testid="error-toast" />)
        })
    } else {
      console.error('Missing required data for assigning task or launching viewer.')
      toast(<Toast message="Cannot assign task: Missing required information." variant="error" />)
    }
  }

  return (
    <TableBody>
      {visibleRows.map((row) => {
        return (
          <TableRow
            tabIndex={-1}
            key={row.task_id}
            sx={{
              borderBottom: '1px solid rgba(220, 227, 229, 1)',
            }}
          >
            <TableCell align="left" data-testid="task-pseudo-id-cell">
              {row.task_pseudo_id}
            </TableCell>
            <TableCell align="left" data-testid="blind-code-cell">
              {row.blind_code}
            </TableCell>
            <TableCell align="left" data-testid="task-type-cell">
              <TaskTypeChip taskType={row.task_type} />
            </TableCell>
            <TableCell align="left" data-testid="priority-cell">
              {row.priority}
            </TableCell>
            <TableCell align="left" data-testid="created-at-cell">
              {formatTaskDate(row.created_at)}
            </TableCell>
            <TableCell align="left" data-testid="due-date-cell">
              {formatTaskDate(row.due_date)}
            </TableCell>
            <TableCell align="left" data-testid="status-cell">
              <TaskStateChip taskState={row.status} />
            </TableCell>
            <TableCell align="right" data-testid="assign-cell">
              <Typography
                sx={{
                  cursor: 'pointer',
                  color: 'secondary.contrastText',
                  typography: 'button',
                }}
                onClick={() => {
                  handleAssignTask(row.task_id, row.task_type)
                }}
              >
                Assign and start
              </Typography>
            </TableCell>
          </TableRow>
        )
      })}
    </TableBody>
  )
}
